import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@kgc/ui';
import {
  AlertTriangle,
  BarChart3,
  CalendarCheck,
  ClipboardList,
  Clock,
  CreditCard,
  Workflow as FallbackIcon,
  FileText,
  FileX,
  Key,
  PackageCheck,
  PenTool,
  Pin,
  ShieldCheck,
  UserPlus,
  Users,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { PublishedWorkflow } from '../types';

const WORKFLOW_ICONS: Record<string, LucideIcon> = {
  Wallet,
  Key,
  Wrench,
  FileText,
  FileX,
  CalendarCheck,
  AlertTriangle,
  ClipboardList,
  PackageCheck,
  CreditCard,
  BarChart3,
  ShieldCheck,
  Users,
  UserPlus,
  PenTool,
};

function getLucideIcon(name?: string | undefined): LucideIcon {
  if (!name) return FallbackIcon;
  return WORKFLOW_ICONS[name] ?? FallbackIcon;
}

interface WorkflowCardProps {
  workflow: PublishedWorkflow;
  isFavorite?: boolean | undefined;
  onToggleFavorite?: (() => void) | undefined;
  onExecute?: (() => void) | undefined;
  onSchedule?: (() => void) | undefined;
}

export function WorkflowCard({
  workflow,
  isFavorite,
  onToggleFavorite,
  onExecute,
  onSchedule,
}: WorkflowCardProps) {
  const { t } = useTranslation();
  const Icon = getLucideIcon(workflow.icon);

  return (
    <Card data-testid={`flow-workflow-card-${workflow.id}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <span className="flex-1">{workflow.name}</span>
          {onToggleFavorite && (
            <button
              type="button"
              onClick={onToggleFavorite}
              data-testid={`flow-pin-btn-${workflow.id}`}
              aria-label={
                isFavorite ? t('integration.flow.unpinWorkflow') : t('integration.flow.pinWorkflow')
              }
              className="ml-auto shrink-0"
            >
              <Pin
                className={cn(
                  'h-4 w-4',
                  isFavorite ? 'text-primary fill-primary' : 'text-muted-foreground'
                )}
              />
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {workflow.steps.length} {t('integration.flow.steps')}
        </p>
        <div className="flex gap-1">
          {onSchedule && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSchedule}
              data-testid={`flow-schedule-btn-${workflow.id}`}
              aria-label={t('integration.flow.schedule.title')}
            >
              <Clock className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}
          <Button
            size="sm"
            onClick={onExecute}
            data-testid={`flow-execute-btn-${workflow.id}`}
            aria-label={`${t('integration.flow.startWorkflow')}: ${workflow.name}`}
          >
            {t('integration.flow.startWorkflow')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
